import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";
import { BookOpen, Loader2 } from "lucide-react";
import { toast } from "sonner";

const SIDE = "https://images.unsplash.com/photo-1533000971552-6a962ff0b9f9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", reason: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user && user !== true) {
      if (user.role === "admin" || user.status === "approved") navigate("/dashboard");
      else navigate("/pending");
    }
  }, [user, navigate]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await register(form);
      toast.success("Request received — welcome!");
      navigate("/pending");
    } catch (e2) {
      setError(apiError(e2));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAF7EE]">
      <div className="flex items-center justify-center px-6 py-12 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <BookOpen className="w-6 h-6 text-[#C46B4E]" strokeWidth={1.6} />
            <span className="font-heading text-2xl">The Peace Plot</span>
          </Link>
          <h1 className="font-heading text-4xl text-[#2A2621]">Request access</h1>
          <p className="text-[#615A50] mt-1">Our collective is invite-only. A leader will welcome you shortly after you join.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-sm font-medium text-[#615A50]">Name</label>
              <input required value={form.name} onChange={set("name")} data-testid="register-name"
                className="mt-1.5 w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#3D5844]/40" placeholder="Your name" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#615A50]">Email</label>
              <input type="email" required value={form.email} onChange={set("email")} data-testid="register-email"
                className="mt-1.5 w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#3D5844]/40" placeholder="you@example.com" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#615A50]">Password</label>
              <input type="password" required value={form.password} onChange={set("password")} data-testid="register-password"
                className="mt-1.5 w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#3D5844]/40" placeholder="Create a password" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#615A50]">Why would you like to join? <span className="text-[#615A50]/60">(optional)</span></label>
              <textarea value={form.reason} onChange={set("reason")} data-testid="register-reason" rows={3}
                className="mt-1.5 w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#3D5844]/40 resize-none" placeholder="Share a little about your faith journey…" />
            </div>
            {error && <p className="text-sm text-[#AC5B40]" data-testid="register-error">{error}</p>}
            <button type="submit" disabled={loading} data-testid="register-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#C46B4E] text-white py-3 font-medium hover:bg-[#AC5B40] transition-colors disabled:opacity-60">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Request to Join
            </button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-[#D5CEBF]" />
            <span className="text-xs text-[#615A50]">or</span>
            <div className="h-px flex-1 bg-[#D5CEBF]" />
          </div>
          <button onClick={handleGoogle} data-testid="register-google"
            className="w-full flex items-center justify-center gap-3 rounded-xl border border-[#D5CEBF] bg-white py-3 font-medium text-[#2A2621] hover:bg-[#F3EFE4] transition-colors">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
            Continue with Google
          </button>

          <p className="mt-6 text-sm text-[#615A50] text-center">
            Already a member?{" "}
            <Link to="/login" data-testid="register-to-login" className="text-[#3D5844] font-medium hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src={SIDE} alt="An open Bible by the water at sunset" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#C46B4E]/30" />
      </div>
    </div>
  );
}
